import { Flex } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import GuestFeedbackForm from '@/components/guest/FeedbackForm';
import InterviewStage from '@/components/guest/InterviewStage';
import ProposeDateForm from '@/components/guest/ProposeDateForm';
import { COMPLETED } from '@/constants/statuses';
import { useInterview } from '@/hooks/queries/useInterview';
import { useUrlState } from '@/hooks/useUrlState';
import Loader from '@/UI/Loader';

import type { InterviewRoom, InterviewTask } from '@/types/interview';

const Guest = () => {
  const [interview, setInterview] = useState<InterviewRoom | null>(null);
  const { history } = useUrlState();
  const params = new URLSearchParams(history.location.search);
  const feedback = params.get('feedback');
  const review = params.get('review');
  const tasks = params.get('tasks');
  const proposed_date = params.get('proposed_date');
  const interviewId = useMemo(
    () => feedback || proposed_date || tasks || null,
    [feedback, proposed_date, tasks],
  );
  const { data: interviewData = null, isPending } = useInterview(interviewId);
  const showFeedback = Boolean(feedback);
  const effectiveInterview = interviewData ?? interview;
  const loading = interviewId ? isPending : false;
  const onComplete = () => {};

  const guestSecondaryView = useMemo<'tasks' | 'dates' | null>(() => {
    if (!effectiveInterview || (!tasks && !proposed_date)) {
      return null;
    }

    const mainInterview = effectiveInterview.current_stage?.tasks?.find(
      (task: InterviewTask) => task.is_planning_task,
    );
    if (mainInterview?.status === COMPLETED) {
      return 'tasks';
    }

    return 'dates';
  }, [effectiveInterview, tasks, proposed_date]);

  useEffect(() => {
    if (interviewData) {
      setInterview(interviewData);
    }
  }, [interviewData]);

  return (
    <Loader spinning={loading}>
      <Flex vertical gap="middle" style={{ maxWidth: 650, width: '100%' }}>
        {effectiveInterview && guestSecondaryView === 'tasks' ? (
          <InterviewStage
            interview={effectiveInterview}
            onComplete={onComplete}
          />
        ) : null}
        {effectiveInterview && guestSecondaryView === 'dates' ? (
          <ProposeDateForm
            interview={effectiveInterview}
            setInterview={setInterview}
          />
        ) : null}
        {effectiveInterview && showFeedback ? (
          <GuestFeedbackForm interview={effectiveInterview} review={review} />
        ) : null}
      </Flex>
    </Loader>
  );
};

export default Guest;
